import React, { useEffect, useRef } from 'react'
import './CrmCover.scss'

/**
 * 登录页左侧封面 —— 复刻 PPT S01 Cover 风格
 * 双层结构:
 *  外层 (.crm-cover-bg + .crm-cover-grid) — IKB 克莱因蓝全屏 + 84px 棋盘网格
 *  内层 (.crm-cover-card)                — 圆角 + 阴影的"终端大小"卡片,内含 ASCII + 文字
 */
const CrmCover: React.FC = () => {
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d')
		if (!ctx) return

		// 字符调色板 (按"密度"递增,模拟噪声值 v → 字符)
		const PALETTE = '   ...:::---+++***◦◦••▢▣'
		const CELL = 18
		const FONT_SIZE = 14

		let dpr = 1
		let w = 0
		let h = 0
		let running = true
		let raf = 0
		const t0 = performance.now()

		const setup = () => {
			dpr = Math.min(window.devicePixelRatio || 1, 2)
			const rect = canvas.getBoundingClientRect()
			if (rect.width < 4 || rect.height < 4) return
			w = rect.width
			h = rect.height
			canvas.width = Math.round(w * dpr)
			canvas.height = Math.round(h * dpr)
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
			ctx.font = `500 ${FONT_SIZE}px "JetBrains Mono", "SF Mono", "Cascadia Code", monospace`
			ctx.textBaseline = 'middle'
			ctx.textAlign = 'center'
		}

		const draw = (t: number) => {
			ctx.clearRect(0, 0, w, h)
			const cols = Math.ceil(w / CELL)
			const rows = Math.ceil(h / CELL)
			for (let r = 0; r < rows; r++) {
				for (let c = 0; c < cols; c++) {
					// 4 个 sin/cos 叠加形成 2D 噪声场 → 字符显隐/字符种类
					const n =
						(Math.sin(c * 0.18 + t) +
							Math.sin(r * 0.24 - t * 0.7) +
							Math.sin((c + r) * 0.12 + t * 0.45) +
							Math.sin(Math.hypot(c - cols * 0.5, r - rows * 0.5) * 0.16 - t * 0.55)) /
						4
					const v = (n + 1) / 2 // [0, 1]
					if (v < 0.22) continue
					const idx = Math.min(PALETTE.length - 1, Math.floor(v * PALETTE.length))
					const ch = PALETTE[idx]
					if (ch === ' ') continue
					const alpha = 0.08 + (v - 0.22) * 0.55
					ctx.fillStyle = `rgba(255, 255, 255, ${alpha.toFixed(3)})`
					ctx.fillText(ch, c * CELL + CELL / 2, r * CELL + CELL / 2)
				}
			}
		}

		const tick = (now: number) => {
			if (!running) return
			const t = ((now - t0) / 1000) * 0.55
			draw(t)
			raf = requestAnimationFrame(tick)
		}

		setup()
		raf = requestAnimationFrame(tick)

		const ro = new ResizeObserver(() => {
			setup()
		})
		ro.observe(canvas)

		return () => {
			running = false
			cancelAnimationFrame(raf)
			ro.disconnect()
		}
	}, [])

	return (
		<div className="crm-cover">
			{/* 外层:IKB 蓝 + 84px 棋盘网格(覆盖整个 Sider) */}
			<div className="crm-cover-bg" aria-hidden />
			<div className="crm-cover-grid" aria-hidden />

			{/* 上层:圆角阴影卡片(终端大小),透明底让外层蓝 + 网格透出,内部放 ASCII 呼吸场 + 文字 */}
			<div className="crm-cover-card">
				<canvas ref={canvasRef} className="crm-cover-canvas" aria-hidden />
				<div className="crm-cover-content">
					<div className="crm-cover-top">
						<div className="crm-cover-label">LEAD · PROJECT PROPOSAL</div>
					</div>

					<div className="crm-cover-center">
						<div className="crm-cover-kicker" data-anim="kicker">
							LEAD · 26.10
						</div>
						<h1 className="crm-cover-title" data-anim="title">
							线索
							<br />
							管理系统
						</h1>
						<div className="crm-cover-rule" />
						<div className="crm-cover-lead" data-anim="lead">
							从多渠道留资到出单归集 · 客户全生命周期调度台
						</div>
					</div>

					<div className="crm-cover-bottom" data-anim="bottom">
						<div className="crm-cover-foot-row">
							<div>基于 React 18 + Semi Design</div>
							<div>→ 登录 / sign in</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default CrmCover
